import MCMain, {
    ChecklistStep,
    ClientCheckList,
    inferMCMainBody,
} from 'modules/home/manage-clients/content/Main';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box } from '@mui/system';

import MCHeaderMain from 'modules/home/manage-clients/header/Main';
import MCAction from 'modules/home/manage-clients/header/ActionClient';
import MCActionContent from 'modules/home/manage-clients/content/ActionClient';
import { Formik } from 'formik';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * ManageClientsPage component.
 * Renders the Manage Clients page.
 * @returns {ReactElement} ManageClientsPage component.
 */
const ManageClientsPage = (): ReactElement => {
    // The status page: view, add or edit
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId } = useParams();
    const innerMCBodyRef = useRef<inferMCMainBody>(null);

    const [statusClientID, setStatusClientID] = useState<string>('');
    const [statusClient, setStatusClient] = useState<string>('');
    const [statusClientEmail, setStatusClientEmail] = useState<string>('');
    const [statusClientPhone, setStatusClientPhone] = useState<number>(0);
    const [statusClientSLADays, setStatusClientSLADays] = useState<number>(0);
    const [statusClientCM9, setStatusClientCM9Days] = useState<number>(0);
    const [statusClientReworkDays, setStatusClientReworkDays] =
        useState<number>(0);
    const [statusClientChecklist, setStatusClientChecklist] = useState<
        ClientCheckList[]
    >([]);
    const [statusClientChecklistStep, setStatusClientChecklistStep] = useState<
        ChecklistStep[]
    >([]);
    const [statusClientArchiveDays, setStatusClientArchiveDays] =
        useState<number>(0);
    const [solicitorData, setSolicitorData] = useState<SolicitorData[]>([]);

    const [statusPage, setStatusPage] = useState<string>('view');
    /**
     * Updates the status page.
     * @param {string} status - The new status page value.
     * @returns {void}
     */
    const updateStatusPage = useCallback(
        (status: string): void => {
            if (status === 'add') {
                navigate('/manage-checklists/add');
            } else if (status === 'edit') {
                navigate(`/manage-checklists/edit/${clientId}`);
            } else {
                navigate(`/manage-checklists`);
            }
            // setStatusPage(status);
        },
        [navigate]
    );

    useEffect(() => {
        /**
         * Resets the solicitor data when statusPage is not "edit".
         * @returns {void}
         */
        const resetSolicitorData = (): void => {
            setSolicitorData([]);
        };

        if (statusPage !== 'edit') {
            resetSolicitorData();
        }
    }, [statusPage]);

    const handleSearchBoxOnSubmit = useCallback(
        (values: FormikSearchPropsValues) => {
            const { current: bodyRef } = innerMCBodyRef;
            if (bodyRef !== null) {
                bodyRef.setSearch(values.search);
            }
        },
        []
    );

    const handleSearchBoxOnReset = useCallback(() => {
        const { current: bodyRef } = innerMCBodyRef;
        if (bodyRef !== null) {
            bodyRef.setSearch('');
        }
    }, []);

    useEffect(() => {
        if (
            clientId &&
            location.pathname === `/manage-checklists/edit/${clientId}`
        ) {
            setStatusClientID(clientId);
            setStatusPage('edit');
        } else if (location.pathname === '/manage-checklists/add') {
            setStatusPage('add');
        } else {
            setStatusPage('view');
        }
    }, [clientId, location.pathname]);
    return (
        <>
            <HeadTag title="Manage Checklists" />
            <Box className="fluid-manage-client-container">
                <Box
                    className={`${
                        statusPage !== 'view'
                            ? 'WrapperHeaderManageClient action-view'
                            : 'WrapperHeaderManageClient'
                    }`}
                >
                    {/* Dynamic Header Manage Clients - View or Edit */}
                    {statusPage === 'view' ? (
                        <Formik
                            initialValues={{
                                search: '',
                                searchBy: '',
                            }}
                            enableReinitialize
                            onSubmit={handleSearchBoxOnSubmit}
                            onReset={handleSearchBoxOnReset}
                        >
                            {() => (
                                <MCHeaderMain
                                    setStatusPage={updateStatusPage}
                                />
                            )}
                        </Formik>
                    ) : (
                        <MCAction
                            setStatusPage={updateStatusPage}
                            statusPage={statusPage}
                            statusClient={statusClient}
                        />
                    )}
                </Box>

                {/* The content - View or Edit */}
                <Box className="manage-client-container">
                    {/* Dynamic Content Manage Clients - View or Edit */}
                    {statusPage === 'view' ? (
                        <MCMain
                            ref={innerMCBodyRef}
                            setStatusPage={setStatusPage}
                            setStatusClient={setStatusClient}
                            setStatusClientEmail={setStatusClientEmail}
                            setStatusClientPhone={setStatusClientPhone}
                            SetStatusClientChecklistStepData={
                                setStatusClientChecklistStep
                            }
                            setStatusClientID={setStatusClientID}
                            setSolicitorData={setSolicitorData}
                            setStatusClientArchiveDays={
                                setStatusClientArchiveDays
                            }
                            setStatusClientCM9Days={setStatusClientCM9Days}
                            setStatusClientReworkDays={
                                setStatusClientReworkDays
                            }
                            setStatusClientSLADays={setStatusClientSLADays}
                            setStatusClientChecklist={setStatusClientChecklist}
                        />
                    ) : (
                        <MCActionContent
                            clientChecklist={statusClientChecklist}
                            clientID={statusClientID}
                            slaDays={statusClientSLADays}
                            cm9Days={statusClientCM9}
                            reworkDays={statusClientReworkDays}
                            archiveDays={statusClientArchiveDays}
                            clientEmail={statusClientEmail}
                            clientName={statusClient}
                            clientPhone={statusClientPhone}
                            clientChecklistStep={statusClientChecklistStep}
                            statusPage={statusPage}
                            setStatusPage={updateStatusPage}
                            clientSolicitorData={solicitorData}
                        />
                    )}
                </Box>
            </Box>
        </>
    );
};

export default ManageClientsPage;
